import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Paper,
  IconButton,
  Collapse,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ServiceTile from './ServiceTile';
import { getIconByName } from '../IconSelector';

// Theme colors
const THEME_COLOR = '#009688';
const THEME_COLOR_LIGHT = '#e0f2f1';

// Tab container — tabs wrap onto multiple rows so a long list of service groups never forces a
// horizontal scroll (and never pushes the page wider than the viewport).
const TabsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 8,
  paddingBottom: 8,
  borderBottom: '2px solid #e0e0e0',
  marginBottom: 16,
}));

// Individual tab button
const TabButton = styled(Box)(({ theme, active, color }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  borderRadius: 4,
  transition: 'all 0.2s ease',
  position: 'relative',
  backgroundColor: active ? `${THEME_COLOR}10` : 'transparent',
  '&:hover': {
    backgroundColor: active ? `${THEME_COLOR}15` : '#f5f5f5',
  },
  '&::after': active ? {
    content: '""',
    position: 'absolute',
    bottom: -10,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: THEME_COLOR,
    borderRadius: '3px 3px 0 0',
  } : {},
}));

// Color dot indicator
const ColorDot = styled(Box)(({ color }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: color || THEME_COLOR,
  flexShrink: 0,
}));

// Section header for groups
const SectionHeader = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 16,
  marginTop: 24,
  '&:first-of-type': {
    marginTop: 0,
  },
}));

// Selected service summary card
const SelectedServiceCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: THEME_COLOR_LIGHT,
  border: `1px solid ${THEME_COLOR}`,
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

// Group icon container
const GroupIconBox = styled(Box)(({ color }) => ({
  width: 36,
  height: 36,
  borderRadius: 8,
  backgroundColor: color || THEME_COLOR,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

const ServiceSelectionPanel = ({
  services = [],
  serviceGroups = [],
  selectedServiceId = null,
  onServiceSelect,
  chargeMapping = {}, // Optional: pricing info per service
  showPricing = true,
  autoCollapse = true, // Auto-collapse panel after service selection
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all' or group id
  const [isExpanded, setIsExpanded] = useState(true); // Controls panel expansion

  // Auto-collapse when service is selected
  useEffect(() => {
    if (selectedServiceId && autoCollapse) {
      setIsExpanded(false);
    }
  }, [selectedServiceId, autoCollapse]);

  // Handle expand/collapse
  const handleChangeService = () => {
    setIsExpanded(true);
  };

  // Group services by their service group
  const groupedServices = useMemo(() => {
    const groups = {};

    services.forEach((service) => {
      const groupId = service.serviceGroupId || service.groupId || 'other';
      const groupName = service.serviceGroupName || service.groupName || 'Other Services';

      if (!groups[groupId]) {
        // Find group info from serviceGroups array
        const groupInfo = serviceGroups.find(g => g.id === groupId) || {};
        groups[groupId] = {
          id: groupId,
          name: groupName,
          icon: groupInfo.icon || null,
          color: groupInfo.color || null,
          services: [],
        };
      }

      // Add charge info if available
      const chargeInfo = chargeMapping[service.id] || {};
      // Normalize service data - API returns serviceName but components expect name
      // Also handle serviceId vs id
      const normalizedService = {
        ...service,
        id: service.id || service.serviceId,
        name: service.name || service.serviceName,
        serviceCharge: chargeInfo.serviceCharge ?? service.serviceCharge ?? 0,
        govtCharge: chargeInfo.govtCharge ?? service.govtCharge ?? 0,
        taxOnServiceCharge: chargeInfo.taxOnServiceCharge ?? service.taxOnServiceCharge ?? 0,
        groupName: groupName,
      };
      groups[groupId].services.push(normalizedService);
    });

    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [services, serviceGroups, chargeMapping]);

  // Filter services based on search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) {
      return groupedServices;
    }

    const lowerSearch = searchTerm.toLowerCase();
    return groupedServices
      .map((group) => ({
        ...group,
        services: group.services.filter((service) => {
          const serviceName = service.name || service.serviceName || '';
          return serviceName.toLowerCase().includes(lowerSearch);
        }),
      }))
      .filter((group) => group.services.length > 0);
  }, [groupedServices, searchTerm]);

  // Get total services count
  const totalServicesCount = useMemo(() => {
    return filteredGroups.reduce((sum, g) => sum + g.services.length, 0);
  }, [filteredGroups]);

  // Get selected service details
  const selectedService = useMemo(() => {
    if (!selectedServiceId) return null;
    for (const group of groupedServices) {
      const service = group.services.find((s) => s.id === selectedServiceId);
      if (service) {
        return { ...service, groupIcon: group.icon, groupColor: group.color };
      }
    }
    return null;
  }, [selectedServiceId, groupedServices]);

  // Handle service selection
  const handleServiceClick = (service) => {
    onServiceSelect && onServiceSelect(service);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `AED ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get groups to display based on active tab
  const displayGroups = useMemo(() => {
    if (activeTab === 'all') {
      return filteredGroups;
    }
    return filteredGroups.filter(g => g.id === activeTab);
  }, [activeTab, filteredGroups]);

  return (
    <Box>
      {/* Selected Service Summary */}
      <Collapse in={Boolean(selectedService)}>
        {selectedService && (
          <SelectedServiceCard elevation={0}>
            <GroupIconBox color={selectedService.groupColor}>
              {selectedService.groupIcon ? (
                React.createElement(getIconByName(selectedService.groupIcon), {
                  sx: { fontSize: 20, color: '#fff' },
                })
              ) : (
                <CategoryIcon sx={{ fontSize: 20, color: '#fff' }} />
              )}
            </GroupIconBox>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: THEME_COLOR }} />
                <Typography variant="caption" sx={{ color: THEME_COLOR, fontWeight: 600 }}>
                  Selected Service
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                {selectedService.name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#616161' }}>
                {selectedService.groupName}
              </Typography>
            </Box>
            {showPricing && (
              <Box sx={{ textAlign: 'right', mr: 2 }}>
                <Typography sx={{ fontSize: 11, color: '#757575' }}>Total</Typography>
                <Typography sx={{ fontWeight: 700, color: THEME_COLOR }}>
                  {formatCurrency(
                    Number(selectedService.serviceCharge || 0) +
                      Number(selectedService.govtCharge || 0) +
                      Number(selectedService.taxOnServiceCharge || 0)
                  )}
                </Typography>
              </Box>
            )}
            {/* Change Service Button - shown when collapsed */}
            {!isExpanded && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleChangeService}
                sx={{
                  borderColor: THEME_COLOR,
                  color: THEME_COLOR,
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: 100,
                  '&:hover': {
                    borderColor: THEME_COLOR,
                    backgroundColor: `${THEME_COLOR}10`,
                  },
                }}
              >
                Change
              </Button>
            )}
          </SelectedServiceCard>
        )}
      </Collapse>

      {/* Collapsible Section: Search, Tabs, and Services */}
      <Collapse in={isExpanded || !selectedServiceId}>
        {/* Search Input */}
        <TextField
          fullWidth
          placeholder="Search services..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: '#fff',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: THEME_COLOR,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9e9e9e' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

      {/* Tabs */}
      <TabsContainer>
        {/* All Services Tab */}
        <TabButton
          active={activeTab === 'all'}
          onClick={() => setActiveTab('all')}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: activeTab === 'all' ? 700 : 500,
              color: activeTab === 'all' ? THEME_COLOR : '#424242',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            All Services
          </Typography>
          <Chip
            label={totalServicesCount}
            size="small"
            sx={{
              height: 20,
              minWidth: 24,
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: activeTab === 'all' ? THEME_COLOR : '#e0e0e0',
              color: activeTab === 'all' ? '#fff' : '#616161',
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </TabButton>

        {/* Individual Group Tabs */}
        {filteredGroups.map((group) => (
          <TabButton
            key={group.id}
            active={activeTab === group.id}
            onClick={() => setActiveTab(group.id)}
          >
            <ColorDot color={group.color} />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: activeTab === group.id ? 700 : 500,
                color: activeTab === group.id ? THEME_COLOR : '#424242',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {group.name}
            </Typography>
            <Chip
              label={group.services.length}
              size="small"
              sx={{
                height: 20,
                minWidth: 24,
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: activeTab === group.id ? (group.color || THEME_COLOR) : '#f5f5f5',
                color: activeTab === group.id ? '#fff' : '#757575',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </TabButton>
        ))}
      </TabsContainer>

        {/* No results */}
        {filteredGroups.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: '#757575',
              border: '1px dashed #e0e0e0',
              borderRadius: 2,
            }}
          >
            <Typography>No services found matching "{searchTerm}"</Typography>
          </Box>
        ) : (
          /* Service Groups with Sections */
          <Box>
            {displayGroups.map((group, groupIndex) => (
              <Box key={group.id}>
                {/* Section Header */}
                <SectionHeader color={group.color}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 1,
                      backgroundColor: group.color || THEME_COLOR,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: '#212121',
                    }}
                  >
                    {group.name}
                  </Typography>
                  <Chip
                    label={group.services.length}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor: group.color ? `${group.color}20` : '#f5f5f5',
                      color: group.color || '#616161',
                    }}
                  />
                </SectionHeader>

                {/* Services Grid */}
                <Grid container spacing={2}>
                  {group.services.map((service) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={service.id}>
                      <ServiceTile
                        service={service}
                        selected={selectedServiceId === service.id}
                        onClick={handleServiceClick}
                        showPricing={showPricing}
                        groupIcon={group.icon}
                        groupColor={group.color}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default ServiceSelectionPanel;
